import { Alert, AlertDescription, AlertIcon, AlertTitle } from "@chakra-ui/react";
import { Bundle, Resource } from "@medplum/fhirtypes";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PatientFHIRData } from "../shared-components/fhir-sections";
import { SummaryContent } from "../shared-components/fhir-sections/summaries/content";
import { PatientTabs } from "../shared-components/tabs";
import { PatientQueryParamProps } from "../shared-logic/consolidated-context/reducer";
import { processBundleWithWorker } from "../shared-logic/useBundleGroupingWorker";
import useRemoteJsonFile from "../shared-logic/useRemoteJsonFile";
import { PreviewRawDocuments } from "./preview-raw-documents";

export type PreviewSectionCounts = {
  sectionCounts: {
    reports: number;
    documents: number;
  };
};

const PROCESS_BUNDLE_QUERY_KEY = "process-bundle";

function PatientOverviewContent({
  url,
  patientQueryParams,
}: {
  url: string | null;
  patientQueryParams?: PatientQueryParamProps;
}) {
  const { data, status, error } = useRemoteJsonFile<Bundle<Resource>>(url);
  const [sidePanelContent, setSidePanelContent] = useState<React.ReactNode | undefined>(undefined);

  const { data: consolidatedState } = useQuery({
    queryKey: [PROCESS_BUNDLE_QUERY_KEY, data?.id],
    queryFn: () => (data ? processBundleWithWorker(data) : undefined),
    enabled: !!data,
    staleTime: 15 * 60 * 1000,
  });

  const isLoadingData = status === "loading" || !consolidatedState;

  if (status === "error") {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Error loading FHIR Bundle</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <PatientTabs
        isIFrame={true}
        mappedConsolidated={consolidatedState}
        isLoadingData={isLoadingData}
      />
      <SummaryContent mappedConsolidated={consolidatedState} />
      <PatientFHIRData
        filters={undefined}
        mappedConsolidated={consolidatedState}
        isConsolidatedLoading={isLoadingData}
        patientQueryParams={patientQueryParams}
        setSidePanelContent={setSidePanelContent}
        sidePanelContent={sidePanelContent}
      />
      {consolidatedState && <PreviewRawDocuments consolidatedState={consolidatedState} />}
    </>
  );
}

export default PatientOverviewContent;
